import React, { type ReactNode, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import {
    BoxShadowMedium,
    ColorNeutralWhite,
    ColorNordicGrey1,
    Spacing8,
    Spacing16,
    Spacing24,
    Spacing40
} from 'autogen/design-tokens/tokens';
import type { MatasPlusOfferDisclaimerQuickViewModelMaybe } from 'autogen/swagger/all';
import { Close } from 'autogen/translation-keys/trans-website-shared';
import { Button } from 'components/Button';
import { Typography } from 'components/generic/typography';
import usePlusOfferNudgeQuickView from 'components/matasPlus/PlusOfferNudge/components/PlusOfferNudgeQuickView/hooks/usePlusOfferNudgeQuickView';
import { useQuickView } from 'components/quickView';
import dynamic from 'helpers/dynamic';
import { toHtmlContent } from 'modules/helpers/strings';
import type { TrackingCategory } from 'modules/tracking/UAEventTracking';
import { breakpoint, sizes } from 'variables';

const QuickView = dynamic(() => import('components/quickView'), { ssr: false });

function getButtonLocationStyling(buttonLocation) {
    switch (buttonLocation) {
        case 'Center':
            return css`
                left: 50%;
                transform: translateX(-50%);
            `;
        case 'Right':
            return css`
                right: ${Spacing8};

                ${breakpoint.up(sizes.xl)} {
                    right: ${Spacing16};
                }
            `;
        default:
            return css`
                left: ${Spacing8};

                ${breakpoint.up(sizes.xl)} {
                    left: ${Spacing16};
                }
            `;
    }
}

const StyledButton = styled(Button)<{ buttonLocation: 'Left' | 'Center' | 'Right'; relative: boolean }>`
    box-shadow: ${BoxShadowMedium};
    border: 1px solid transparent;

    ${({ relative, buttonLocation }) =>
        !relative &&
        css`
            position: absolute;
            bottom: ${Spacing8};
            ${breakpoint.up(sizes.xl)} {
                bottom: ${Spacing16};
            }
            ${getButtonLocationStyling(buttonLocation)}
        `}

    &:hover {
        background: ${ColorNeutralWhite};
        border: 1px solid ${ColorNordicGrey1};
    }
`;

const ModalContent = styled.div`
    padding: ${Spacing8} ${Spacing40} ${Spacing40};
    display: flex;
    flex-direction: column;
    width: 100%;
    overflow-y: auto;
    max-width: 1000px;
`;

const CloseButton = styled(Button)`
    margin-top: ${Spacing24};
`;

interface IDisclaimerButtonProps {
    disclaimerText?: string | ReactNode;
    buttonText?: string;
    buttonLocation?: 'Left' | 'Center' | 'Right';
    quickViewId: string;
    trackingCategory?: TrackingCategory;
    relative?: boolean;
    disclaimerQuickViewModel?: MatasPlusOfferDisclaimerQuickViewModelMaybe;
}

export function DisclaimerButton(props: IDisclaimerButtonProps): JSX.Element {
    const {
        disclaimerText,
        buttonText,
        buttonLocation = 'Right',
        quickViewId,
        trackingCategory,
        relative = false,
        disclaimerQuickViewModel,
        ...rest
    } = props;

    const { t } = useTranslation();
    const { toggle } = useQuickView(quickViewId);
    const [, togglePlusOfferNudge] = usePlusOfferNudgeQuickView();

    function toggleQuickView(visible: boolean) {
        if (disclaimerQuickViewModel) {
            togglePlusOfferNudge({
                isOpen: true,
                model: disclaimerQuickViewModel
            });
        } else {
            toggle(visible);
        }
    }

    if (!disclaimerText) return <>{null}</>;

    return (
        <>
            <StyledButton
                buttonLocation={buttonLocation}
                variant="negative"
                size="sm"
                onClick={(event) => {
                    event.preventDefault(); // Stops navigation if nested in an anchor.
                    toggleQuickView(true);
                }}
                relative={relative}
                {...rest}
            >
                {buttonText}
            </StyledButton>
            <Suspense fallback={null}>
                <QuickView quickViewId={quickViewId}>
                    <ModalContent>
                        <Typography.Body size="L">
                            {typeof disclaimerText === 'string'
                                ? toHtmlContent(disclaimerText.replaceAll('\n', '<br/>'))
                                : disclaimerText}
                        </Typography.Body>
                        <CloseButton variant="primary" onClick={() => toggleQuickView(false)}>
                            {t(Close)}
                        </CloseButton>
                    </ModalContent>
                </QuickView>
            </Suspense>
        </>
    );
}

export default DisclaimerButton;
